import {
  Checkbox,
  EmptyButton,
  KeyValueCustomSelect,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RoundButton,
  roundButtonStylesMedium,
} from "@smartsuite/react-ui";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";
import { useRootStore } from "../../../stores/hooks";
import type { UpdateSettings, FieldMatchType } from "../types";
import { InfoIcon } from "../InfoIcon/InfoIcon";
import { t } from "i18next";
import * as S from "./style";

interface InfoIconButtonProps {
  onClose: () => void;
  onSave: (settigs: UpdateSettings) => void;
  updateSettings: UpdateSettings | undefined;
}

type MatchOption = Array<{
  value: FieldMatchType;
  label: string;
}>;

export const UpdateModal: FunctionComponent<InfoIconButtonProps> = ({
  onClose,
  onSave,
  updateSettings,
}) => {
  const { applications } = useRootStore();
  const [options, setOptions] = useState<MatchOption>([]);

  const [settings, setSettings] = useState<UpdateSettings>({
    matchFieldType: "title",
    createUnmatchedRecords: false,
  });

  useEffect(() => {
    if (updateSettings !== undefined) {
      setSettings(updateSettings);
    }
  }, [updateSettings]);

  useEffect(() => {
    const options: MatchOption = [
      {
        value: "title",
        label:
          (applications.selectedApplication?.structure.find(
            (field) => field.params.primary === true,
          )?.label ?? "Title") + ` (${t("import.primary-field")})`,
      },
    ];

    const recordidField = applications.selectedApplication?.structure.find(
      (field) => field.field_type === "recordidfield",
    );

    if (recordidField && !recordidField.params.hidden) {
      options.push({
        value: "id",
        label: recordidField.label,
      });
    }

    const autonumberField = applications.selectedApplication?.structure.find(
      (field) => field.field_type === "autonumberfield",
    );

    if (autonumberField) {
      options.push({
        value: "autonumber",
        label: autonumberField.label,
      });
    }

    setOptions(options);
  }, []);

  return (
    <ModalOverlay animation="none" active={true} closeByEsc={false}>
      <Modal
        styles={{}}
        size="S"
        header={<ModalHeader title={t("import.update-records")} />}
        onClose={onClose}
        footer={
          <ModalFooter
            buttons={
              <>
                <S.CancelWrapper>
                  <EmptyButton size="lg" onClick={onClose}>
                    Cancel
                  </EmptyButton>
                </S.CancelWrapper>
                <RoundButton
                  styles={roundButtonStylesMedium}
                  onClick={() => {
                    onSave(settings);
                  }}
                  disabled={false}
                >
                  Save
                </RoundButton>
              </>
            }
          ></ModalFooter>
        }
      >
        <S.Wrapper>
          <S.Info>{t("import.update-message")}</S.Info>
          <S.Info>{t("import.merge-field-required")}</S.Info>
          <S.Space />
          <S.Label>{t("import.merge-field")}</S.Label>
          <KeyValueCustomSelect
            my="left top+2"
            disabled={false}
            value={settings.matchFieldType}
            showClear={false}
            placeholderArrowStyle="filled"
            onChange={(value) => {
              setSettings({
                ...settings,
                matchFieldType: value as FieldMatchType,
              });
            }}
            options={options}
          />
          <S.Space />
          <S.CheckboxView>
            <Checkbox
              className="checkbox-rect"
              checked={settings.createUnmatchedRecords}
              renderLabelTag={true}
              ariaLabel={t("import.create-unmatched-records")}
              onChange={(checked: boolean) => {
                setSettings({
                  ...settings,
                  createUnmatchedRecords: checked,
                });
              }}
            />
            <S.CheckboxText
              onClick={() => {
                setSettings({
                  ...settings,
                  createUnmatchedRecords: !settings.createUnmatchedRecords,
                });
              }}
            >
              Create unmatched records
            </S.CheckboxText>
            <InfoIcon
              placements={["right"]}
              tooltipText="When checked, system will skip cells with errors, but will import the remaining part of a record. The entire record will be skipped otherwise."
            />
          </S.CheckboxView>
          <S.Space />
        </S.Wrapper>
      </Modal>
    </ModalOverlay>
  );
};
